import React, { useState } from 'react';

import { Button } from '@/components/ui/button';
import { Field, Label } from '@/components/ui/catalyst/fieldset';
import { Input } from '@/components/ui/catalyst/input';
import { Textarea } from '@/components/ui/catalyst/textarea';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { IntakePreferenceSection } from '@/services/types/firms-types';

// Suggested section titles
const SUGGESTED_TITLES = [
  'Intake Requirements',
  'Required Documentation',
  'Systems',
  'Firm Info',
];

interface AddSectionDialogProps {
  onAdd: (section: IntakePreferenceSection) => void;
  triggerLabel?: string;
}

export const AddSectionDialog: React.FC<AddSectionDialogProps> = ({
  onAdd,
  triggerLabel = 'Add Section',
}) => {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');

  const handleSave = (): void => {
    const trimmedTitle = title.trim();
    if (!trimmedTitle) return;

    onAdd({ title: trimmedTitle, content: content.trim() });
    setOpen(false);
    setTitle('');
    setContent('');
  };

  const handleCancel = (): void => {
    setOpen(false);
    setTitle('');
    setContent('');
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="secondary">
          {triggerLabel}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Add Section</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <Field>
            <Label htmlFor="section_title">Section Title</Label>
            <Input
              id="section_title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Intake Requirements"
            />
            <div className="mt-2 flex flex-wrap gap-2">
              <span className="text-xs text-gray-500">Quick fills:</span>
              {SUGGESTED_TITLES.map((suggestion) => (
                <button
                  key={suggestion}
                  type="button"
                  onClick={() => setTitle(suggestion)}
                  className="text-xs px-2 py-1 rounded bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </Field>
          <Field>
            <Label htmlFor="section_content">Content</Label>
            <Textarea
              id="section_content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={6}
              placeholder="Enter section content..."
            />
          </Field>
        </div>
        <DialogFooter className="mt-4">
          <Button variant="ghost" onClick={handleCancel}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!title.trim()}>
            Add Section
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
